import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts';
import { Loader2, MessageSquare, Paperclip, Users, TrendingUp, AlertCircle, User, Tag as TagIcon, Phone, X, ChevronDown, Check } from 'lucide-react';
import CopyablePhone from '../CopyablePhone';
// MultiSelect Component
const MultiSelect = ({ options, selectedValues, onChange, placeholder, icon: Icon, getLabel, getValue }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleOption = (value) => {
        if (selectedValues.includes(value)) {
            onChange(selectedValues.filter(v => v !== value));
        } else {
            onChange([...selectedValues, value]);
        }
    };

    const displayText = selectedValues.length === 0 
        ? placeholder 
        : selectedValues.length === 1
            ? (options.find(opt => getValue(opt) === selectedValues[0])?.[getLabel] || 
               options.find(opt => getValue(opt) === selectedValues[0])?.email || 
               selectedValues[0])
            : `${selectedValues.length} seleccionados`;

    return (
        <div className="flex items-center gap-2" ref={dropdownRef}>
            {Icon && <Icon size={14} className="text-slate-400" />}
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500 flex items-center gap-2 min-w-[180px] justify-between hover:bg-slate-750 transition-colors"
                >
                    <span className={selectedValues.length === 0 ? 'text-slate-400' : ''}>
                        {displayText}
                    </span>
                    <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                
                {isOpen && (
                    <div className="absolute top-full left-0 mt-1 w-full min-w-[250px] bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 max-h-[300px] overflow-y-auto">
                        {options.map(option => {
                            const value = getValue(option);
                            const label = option[getLabel];
                            const isSelected = selectedValues.includes(value);
                            
                            return (
                                <label
                                    key={value}
                                    className="flex items-center gap-2 px-3 py-2 hover:bg-slate-700 cursor-pointer transition-colors"
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleOption(value)}
                                        className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500 focus:ring-2"
                                    />
                                    <span className="text-sm text-slate-200 flex-1">{label}</span>
                                    {isSelected && <Check size={14} className="text-blue-400" />}
                                </label>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
const GRANULARITIES = [
    { value: 'day', label: 'Día' },
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
];

const CHARTS = [
    {
        key: 'conversations_created',
        title: 'Conversaciones creadas',
        color: '#3b82f6',
        icon: MessageSquare,
        description: 'Total de conversaciones nuevas por período',
    },
    {
        key: 'messages_inbound',
        title: 'Mensajes recibidos',
        color: '#10b981',
        icon: TrendingUp,
        description: 'Mensajes entrantes (del cliente) por período',
    },
    {
        key: 'avg_messages_per_conversation',
        title: 'Mensajes promedio / conversación',
        color: '#8b5cf6',
        icon: MessageSquare,
        description: 'Promedio de mensajes por conversación (agrupado por fecha de creación)',
        valueDecimals: 1,
    },
    {
        key: 'attachments',
        title: 'Adjuntos enviados',
        color: '#f59e0b',
        icon: Paperclip,
        description: 'Archivos adjuntos totales por período',
    },
    {
        key: 'outbound_unanswered',
        title: 'Outbound sin respuesta',
        color: '#ef4444',
        icon: AlertCircle,
        description: 'Conversaciones iniciadas por agente que no recibieron respuesta',
    },
];

// Multi-series chart configuration
const MULTI_SERIES_CHART = {
    key: 'inbound_analysis',
    title: 'Conversaciones iniciadas por el cliente',
    icon: Users,
    description: 'Análisis de conversaciones iniciadas por el cliente',
    series: [
        {
            key: 'inbound_initiated',
            label: 'Total iniciadas por cliente',
            color: '#06b6d4',
        },
        {
            key: 'inbound_initiated_google',
            label: 'Con tag Google',
            color: '#34d399',
        },
        {
            key: 'inbound_initiated_unanswered',
            label: 'Sin respuesta',
            color: '#fb923c',
        },
    ],
};

const formatPeriodLabel = (period, granularity) => {
    if (!period) return '';
    try {
        const d = new Date(period);
        if (granularity === 'month') {
            return d.toLocaleDateString('es-ES', { month: 'short', year: 'numeric', timeZone: 'UTC' });
        }
        if (granularity === 'week') {
            return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', timeZone: 'UTC' });
        }
        return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', timeZone: 'UTC' });
    } catch {
        return period;
    }
};

const toDateInputValue = (date) => {
    return date.toISOString().split('T')[0];
};

const getChannelLabel = (channel) => {
    const customName = typeof channel.custom_name === 'object'
        ? channel.custom_name?.name
        : channel.custom_name;

    return customName || channel.display_phone_number || channel.id;
};

const SingleChart = ({ config, data, granularity }) => {
    const Icon = config.icon;
    const chartData = (data || []).map(d => ({
        period: d.period,
        label: formatPeriodLabel(d.period, granularity),
        value: d.value,
    }));

    const total = chartData.reduce((sum, d) => sum + Number(d.value), 0);
    const formattedTotal = config.valueDecimals
        ? (total / Math.max(chartData.length, 1)).toFixed(config.valueDecimals)
        : total.toLocaleString('es-ES');

    return (
        <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-5 flex flex-col gap-4">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: config.color + '1a', border: `1px solid ${config.color}33` }}>
                        <Icon size={18} style={{ color: config.color }} />
                    </div>
                    <div>
                        <h3 className="font-semibold text-slate-100 text-sm leading-tight">{config.title}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{config.description}</p>
                    </div>
                </div>
                <div className="text-right">
                    <span className="text-2xl font-bold" style={{ color: config.color }}>{formattedTotal}</span>
                    <p className="text-xs text-slate-500">{config.valueDecimals ? 'promedio' : 'total'}</p>
                </div>
            </div>

            {chartData.length === 0 ? (
                <div className="h-40 flex items-center justify-center text-slate-500 text-sm">
                    Sin datos para el período seleccionado
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={160}>
                    <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <defs>
                            <linearGradient id={`grad-${config.key}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={config.color} stopOpacity={0.25} />
                                <stop offset="95%" stopColor={config.color} stopOpacity={0.02} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                        <XAxis
                            dataKey="label"
                            tick={{ fill: '#64748b', fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            interval="preserveStartEnd"
                        />
                        <YAxis
                            tick={{ fill: '#64748b', fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            allowDecimals={!!config.valueDecimals}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: '#1e293b',
                                border: '1px solid #334155',
                                borderRadius: '8px',
                                color: '#f1f5f9',
                                fontSize: '12px',
                            }}
                            itemStyle={{ color: config.color }}
                            labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                            formatter={(value) => [
                                config.valueDecimals ? Number(value).toFixed(config.valueDecimals) : value,
                                config.title,
                            ]}
                        />
                        <Area
                            type="monotone"
                            dataKey="value"
                            stroke={config.color}
                            strokeWidth={2}
                            fill={`url(#grad-${config.key})`}
                            dot={false}
                            activeDot={{ r: 4, fill: config.color, strokeWidth: 0 }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

const MultiSeriesChart = ({ config, data, granularity }) => {
    const Icon = config.icon;
    
    // Merge all periods from all series
    const allPeriods = new Set();
    config.series.forEach(serie => {
        (data?.[serie.key] || []).forEach(d => allPeriods.add(d.period));
    });
    
    // Create combined data structure
    const chartData = Array.from(allPeriods).sort().map(period => {
        const item = {
            period,
            label: formatPeriodLabel(period, granularity),
        };
        
        config.series.forEach(serie => {
            const dataPoint = (data?.[serie.key] || []).find(d => d.period === period);
            item[serie.key] = dataPoint ? dataPoint.value : 0;
        });
        
        return item;
    });

    // Calculate totals for each series
    const totals = {};
    config.series.forEach(serie => {
        totals[serie.key] = chartData.reduce((sum, d) => sum + Number(d[serie.key] || 0), 0);
    });

    return (
        <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-5 flex flex-col gap-4">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-500/10 border border-blue-500/30">
                        <Icon size={18} className="text-blue-400" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-slate-100 text-sm leading-tight">{config.title}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{config.description}</p>
                    </div>
                </div>
                <div className="text-right">
                    <div className="flex flex-col gap-1">
                        {config.series.map(serie => (
                            <div key={serie.key} className="flex items-center gap-2 text-xs">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: serie.color }}></div>
                                <span className="text-slate-400">{serie.label}:</span>
                                <span className="font-semibold" style={{ color: serie.color }}>
                                    {totals[serie.key].toLocaleString('es-ES')}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {chartData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-slate-500 text-sm">
                    Sin datos para el período seleccionado
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <defs>
                            {config.series.map(serie => (
                                <linearGradient key={serie.key} id={`grad-${serie.key}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={serie.color} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={serie.color} stopOpacity={0.02} />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                        <XAxis
                            dataKey="label"
                            tick={{ fill: '#64748b', fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            interval="preserveStartEnd"
                        />
                        <YAxis
                            tick={{ fill: '#64748b', fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: '#1e293b',
                                border: '1px solid #334155',
                                borderRadius: '8px',
                                color: '#f1f5f9',
                                fontSize: '12px',
                            }}
                            labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                        />
                        <Legend 
                            wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                            iconType="circle"
                            formatter={(value) => {
                                const serie = config.series.find(s => s.key === value);
                                return serie ? serie.label : value;
                            }}
                        />
                        {config.series.map(serie => (
                            <Area
                                key={serie.key}
                                type="monotone"
                                dataKey={serie.key}
                                stroke={serie.color}
                                strokeWidth={2}
                                fill={`url(#grad-${serie.key})`}
                                dot={false}
                                activeDot={{ r: 4, fill: serie.color, strokeWidth: 0 }}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

const WhatsAppAnalytics = () => {
    const { authFetch } = useAuth();

    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const [granularity, setGranularity] = useState('day');
    const [fromDate, setFromDate] = useState(toDateInputValue(thirtyDaysAgo));
    const [toDate, setToDate] = useState(toDateInputValue(today));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Filter states
    const [agentIds, setAgentIds] = useState([]);
    const [tagIds, setTagIds] = useState([]);
    const [channelId, setChannelId] = useState('');
    
    // Filter options
    const [agents, setAgents] = useState([]);
    const [tags, setTags] = useState([]);
    const [channels, setChannels] = useState([]);
    const selectedChannel = channels.find(channel => channel.id === channelId);

    // Fetch filter options
    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const [agentsRes, tagsRes, channelsRes] = await Promise.all([
                    authFetch('/api/backoffice/users'),
                    authFetch('/api/tags/?all=true'),
                    authFetch('/api/admin/channels')
                ]);

                if (agentsRes.ok) {
                    const agentsData = await agentsRes.json();
                    setAgents(agentsData.users || agentsData || []);
                }

                if (tagsRes.ok) {
                    const tagsData = await tagsRes.json();
                    setTags(tagsData || []);
                }

                if (channelsRes.ok) {
                    const channelsData = await channelsRes.json();
                    setChannels(channelsData.channels || channelsData || []);
                }
            } catch (err) {
                console.error('Error fetching filter options:', err);
            }
        };

        fetchFilterOptions();
    }, [authFetch]);

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams({ granularity, from_date: fromDate, to_date: toDate });
            agentIds.forEach(id => params.append('agent_id', id));
            tagIds.forEach(id => params.append('tag_id', id));
            if (channelId) params.append('channel_id', channelId);
            const res = await authFetch(`/api/backoffice/whatsapp/analytics?${params}`);
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `Error ${res.status}`);
            }
            const json = await res.json();
            setData(json);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [authFetch, granularity, fromDate, toDate, agentIds, tagIds, channelId]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    return (
        <div className="p-6 space-y-6 overflow-y-auto h-full">
            {/* Header */}
            <div className="flex flex-col gap-1">
                <h1 className="text-2xl font-bold text-slate-50 tracking-tight">WhatsApp Analytics</h1>
                <p className="text-slate-400 text-sm">Evolución del uso de WhatsApp a lo largo del tiempo</p>
            </div>

            {/* Filters */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-2xl p-4 space-y-4">
                {/* Date and Granularity Row */}
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-400 font-medium">Desde</label>
                        <input
                            type="date"
                            value={fromDate}
                            max={toDate}
                            onChange={e => setFromDate(e.target.value)}
                            className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-400 font-medium">Hasta</label>
                        <input
                            type="date"
                            value={toDate}
                            min={fromDate}
                            onChange={e => setToDate(e.target.value)}
                            className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1">
                        {GRANULARITIES.map(g => (
                            <button
                                key={g.value}
                                onClick={() => setGranularity(g.value)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                                    granularity === g.value
                                        ? 'bg-blue-600 text-white shadow-sm'
                                        : 'text-slate-400 hover:text-slate-200'
                                }`}
                            >
                                {g.label}
                            </button>
                        ))}
                    </div>
                    {loading && <Loader2 size={18} className="animate-spin text-blue-400 ml-auto" />}
                </div>

                {/* Additional Filters Row */}
                <div className="flex flex-wrap items-center gap-3">
                    {/* Agent Filter */}
                    <MultiSelect
                        options={agents}
                        selectedValues={agentIds}
                        onChange={setAgentIds}
                        placeholder="Todos los agentes"
                        icon={User}
                        getLabel="full_name"
                        getValue={(agent) => agent.email}
                    />

                    {/* Tag Filter */}
                    <MultiSelect
                        options={tags}
                        selectedValues={tagIds}
                        onChange={setTagIds}
                        placeholder="Todas las etiquetas"
                        icon={TagIcon}
                        getLabel="name"
                        getValue={(tag) => tag.id}
                    />

                    {/* Channel Filter */}
                    <div className="flex items-center gap-2">
                        <Phone size={14} className="text-slate-400" />
                        <select
                            value={channelId}
                            onChange={(e) => setChannelId(e.target.value)}
                            className="bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500"
                        >
                            <option value="">Todos los canales</option>
                            {channels.map(channel => (
                                <option key={channel.id} value={channel.id}>
                                    {getChannelLabel(channel)}
                                </option>
                            ))}
                        </select>
                        {selectedChannel?.display_phone_number && (
                            <CopyablePhone phone={selectedChannel.display_phone_number} showText={false} />
                        )}
                    </div>

                    {/* Clear Filters Button */}
                    {(agentIds.length > 0 || tagIds.length > 0 || channelId) && (
                        <button
                            onClick={() => {
                                setAgentIds([]);
                                setTagIds([]);
                                setChannelId('');
                            }}
                            className="flex items-center gap-1 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm rounded-lg transition-colors"
                            title="Limpiar filtros"
                        >
                            <X size={14} />
                            Limpiar
                        </button>
                    )}
                </div>
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-900/20 border border-red-800 text-red-300 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}

            {/* Charts grid */}
            {!loading && data && (
                <>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        {CHARTS.map(config => (
                            <SingleChart
                                key={config.key}
                                config={config}
                                data={data.series?.[config.key]}
                                granularity={granularity}
                            />
                        ))}
                    </div>
                    
                    {/* Multi-series chart for inbound analysis */}
                    <MultiSeriesChart
                        config={MULTI_SERIES_CHART}
                        data={data.series}
                        granularity={granularity}
                    />
                </>
            )}

            {loading && !data && (
                <div className="flex items-center justify-center py-20 text-slate-500 gap-3">
                    <Loader2 size={24} className="animate-spin" />
                    <span>Cargando datos...</span>
                </div>
            )}
        </div>
    );
};

export default WhatsAppAnalytics;
